import { useTranslation } from "react-i18next";
import { AmwayFilledLogoIcon } from "../../icons/AmwayFilledLogoIcon/AmwayFilledLogoIcon";
import LosLineage from "./LosLineage/LosLineage";
import { SetStateAction, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import defaultContent from "../../../content/downlineProfile";
import LOSMarker from "./LosMarker";
import "./DownlineProfileLOS.scss";
import { useGetAboLineageQuery } from "../../../services/AboProfile/AboProfileAPI";
import type { AboLineageEntry } from "../../../services/AboProfile/AboProfileAPI.types";
import type { KpiId } from "../../../services/AboEnrichment/AboEnrichmentAPI.types";
import { trackLineageModalToggle } from "../../../components/DownlineProfile/PlatinumProfile/downlineProfileTealium";
import LeafCircleLoader from "../../leaf/LeafLoadingSkeleton/LeafCircleLoader/LeafCircleLoader";
import useGetProfileImage from "../../../components/DownlineProfile/hooks/useGetProfileImage";
import { useAppSelector } from "../../../store";
import { useLockBodyScrolling } from "../../util/cssUtils";

// The currently-viewed ABO is the root of the LOS when the last lineage entry's losNumber is 1.
export const isRootLineage = (lineage?: AboLineageEntry[]): boolean => Number((lineage ?? []).at(-1)?.losNumber) === 1;

// A frontline ABO (losNumber 2) is directly linked to the viewer, so there is no upline path to show.
export const isFrontlineLineage = (lineage?: AboLineageEntry[]): boolean =>
  Number((lineage ?? []).at(-1)?.losNumber) === 2;

interface DownlineProfileIconProps {
  variant?: "profile" | "list";
  aff?: string;
  abo?: string;
  setSelectedAffAbo?: React.Dispatch<SetStateAction<string>>;
  pushToHistory?: (affAbo: string) => void;
  aboOnclick?: (affAbo: string) => void;
  onLineageOpenChange?: (isOpen: boolean) => void;
  includeGlobalMB?: boolean;
  kpis?: KpiId[];
  imageRefUrl?: string;
  isCustomer?: boolean;
}

const DownlineProfileIcon = ({
  variant = "profile",
  aff = "",
  abo = "",
  setSelectedAffAbo,
  pushToHistory,
  aboOnclick,
  onLineageOpenChange,
  includeGlobalMB,
  kpis,
  imageRefUrl = "",
  isCustomer = false,
}: DownlineProfileIconProps) => {
  const [showLineage, setShowLineage] = useState(false);
  const isFromEdLos = useAppSelector((s) => s.profile.isFromEdLos);
  const edlosRootAffAbo = useAppSelector((s) => s.profile.edlosRootAffAbo);
  const edlosRootDepth = useAppSelector((s) => s.profile.edlosRootDepth);
  const { t } = useTranslation(["downlineProfile"]);
  const skip = !aff || !abo;
  const portalRef = useRef<HTMLDivElement>(null);

  const { data, isFetching } = useGetAboLineageQuery(
    {
      aff,
      abo,
      IncludeGlobalMB: includeGlobalMB,
      IsEdlos: isFromEdLos,
      ...(isFromEdLos && edlosRootAffAbo ? { EdlosRootAffAbo: edlosRootAffAbo } : {}),
      ...(isFromEdLos && edlosRootDepth !== null ? { EdlosRootDepth: edlosRootDepth } : {}),
    },
    { skip },
  );
  const { profileImage, isLoading: profileImageLoading } = useGetProfileImage([
    { affAbo: `${aff}-${abo}`, imageRefUrl },
  ]);
  const lineage = data?.lineage;
  const isRootAbo = isRootLineage(lineage);
  const hasNoLineage = isRootAbo || (variant === "profile" && isFrontlineLineage(lineage));
  const selectedLineageEntry = (lineage ?? []).at(-1);
  const showLosLineage = showLineage && !hasNoLineage;

  const handleToggle = () => {
    setShowLineage((prev) => !prev);
    if (variant === "profile" && showLineage) {
      trackLineageModalToggle("close");
    } else if (variant === "profile" && !showLineage) {
      trackLineageModalToggle("open");
    }
  };

  useEffect(() => {
    onLineageOpenChange?.(showLosLineage);
  }, [showLosLineage, onLineageOpenChange]);

  useLockBodyScrolling(variant === "list" && showLosLineage, portalRef, undefined, {
    scrollNeutral: variant === "list",
  });
  const closeLineageButton = (
    <button
      className="downlineProfileIconWrapper--block"
      onClick={() => {
        setShowLineage(false);
        if (variant === "profile") {
          trackLineageModalToggle("close");
        }
      }}
      aria-label={t("closeLineage", defaultContent.closeLineage)}
    />
  );

  const losLineage = (
    <LosLineage
      lineage={lineage}
      setSelectedAffAbo={setSelectedAffAbo}
      setShowLineage={setShowLineage}
      pushToHistory={pushToHistory}
      aboOnclick={aboOnclick}
      compact={variant === "list"}
      kpis={kpis}
      isCustomer={isCustomer}
    />
  );

  return (
    <>
      <div className={`downlineProfileIconWrapper${variant === "list" ? " downlineProfileIconWrapper--list" : ""}`}>
        <button
          className={`downlineProfileIcon${variant === "list" ? " downlineProfileIcon--list" : ""}`}
          onClick={handleToggle}
          disabled={isFetching || hasNoLineage || profileImageLoading}
          aria-label={variant === "list" ? t("viewTheirLOS", defaultContent.viewTheirLOS) : undefined}
        >
          {variant === "profile" && (
            <>
              {isFetching || profileImageLoading ? (
                <>
                  <span className="downlineProfileIcon__person">
                    <LeafCircleLoader isLoading={isFetching || profileImageLoading} width="100%" height="100%" />
                  </span>
                  <span className="downlineProfileIcon__user">
                    <LeafCircleLoader isLoading={isFetching || profileImageLoading} width="100%" height="100%" />
                  </span>
                </>
              ) : (
                <>
                  {profileImage ? (
                    <>
                      <span className="downlineProfileIcon__person">
                        <img src={profileImage} alt="" />
                      </span>
                      <span className="downlineProfileIcon__user">
                        <img src={profileImage} alt="" />
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="downlineProfileIcon__person">
                        <AmwayFilledLogoIcon />
                      </span>
                      <span className="downlineProfileIcon__user">
                        <AmwayFilledLogoIcon />
                      </span>
                    </>
                  )}
                </>
              )}
            </>
          )}
          <LOSMarker
            lineageNumber={selectedLineageEntry?.losNumber}
            className={`downlineProfileIcon--losMarker${variant === "list" ? " downlineProfileIcon--losMarker--list" : ""}`}
            isLoading={isFetching}
            toggleFlip={showLineage}
            noLineage={hasNoLineage}
          />
        </button>
        {variant === "profile" && showLosLineage && losLineage}
      </div>
      {variant === "profile" && showLosLineage && closeLineageButton}
      {variant === "list" &&
        showLosLineage &&
        createPortal(
          <>
            {closeLineageButton}
            <div
              ref={portalRef}
              className="downlineProfileIconWrapper downlineProfileIconWrapper--list downlineProfileIconWrapper--portal"
            >
              {losLineage}
            </div>
          </>,
          document.body,
        )}
    </>
  );
};

export default DownlineProfileIcon;
